import { useCallback } from "react";
import { useAuthFetch } from "../../utils/useAuthFetch";
import {
  DashboardDrilldownResponse,
  DashboardListResponse,
  DashboardModuleDetailResponse,
  DashboardPanelResponse,
  DashboardRange,
} from "./types";

export const useOrganizationDashboardsApi = () => {
  const authFetch = useAuthFetch();
  const authFetchResponse = useAuthFetch("response");

  const listDashboards = useCallback(
    (organizationId: string): Promise<DashboardListResponse> =>
      authFetch(
        `/api/organization/${encodeURIComponent(organizationId)}/dashboards`,
        "load module dashboards",
      ),
    [authFetch],
  );

  const getModuleDashboard = useCallback(
    (
      organizationId: string,
      moduleId: string,
    ): Promise<DashboardModuleDetailResponse> =>
      authFetch(
        `/api/organization/${encodeURIComponent(organizationId)}/dashboards/${encodeURIComponent(moduleId)}`,
        "load module dashboard",
      ),
    [authFetch],
  );

  const getPanel = useCallback(
    (
      organizationId: string,
      moduleId: string,
      panelId: string,
      range: DashboardRange,
    ): Promise<DashboardPanelResponse> => {
      const params = new URLSearchParams({
        from: range.from,
        to: range.to,
        interval: range.interval,
      });
      return authFetch(
        `/api/organization/${encodeURIComponent(organizationId)}/dashboards/${encodeURIComponent(moduleId)}/${encodeURIComponent(panelId)}?${params.toString()}`,
        "load dashboard panel",
      );
    },
    [authFetch],
  );

  const getDrilldown = useCallback(
    (
      organizationId: string,
      moduleId: string,
      panelId: string,
      range: DashboardRange,
      page: number,
      pageSize: number,
      sort: { key: string; direction: "asc" | "desc" },
    ): Promise<DashboardDrilldownResponse> => {
      const params = new URLSearchParams({
        from: range.from,
        to: range.to,
        page: String(page),
        page_size: String(pageSize),
        sort: sort.key,
        direction: sort.direction,
      });
      return authFetch(
        `/api/organization/${encodeURIComponent(organizationId)}/dashboards/${encodeURIComponent(moduleId)}/${encodeURIComponent(panelId)}/drilldown?${params.toString()}`,
        "load dashboard detail rows",
      );
    },
    [authFetch],
  );

  const downloadCsv = useCallback(
    async (href: string): Promise<Blob> => {
      const response = (await authFetchResponse(
        href,
        "download dashboard CSV",
      )) as Response;
      return response.blob();
    },
    [authFetchResponse],
  );

  return {
    listDashboards,
    getModuleDashboard,
    getPanel,
    getDrilldown,
    downloadCsv,
  };
};

export const dashboardPanelErrorFrom = (error: unknown) => {
  if (!error || typeof error !== "object") return null;
  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") return null;
  const detail = (response as { detail?: unknown }).detail;
  if (!detail || typeof detail !== "object") return null;
  const item = detail as Record<string, unknown>;
  if (
    typeof item.code !== "string" ||
    typeof item.message !== "string" ||
    typeof item.correlation_id !== "string"
  ) {
    return null;
  }
  return {
    code: item.code,
    message: item.message,
    correlation_id: item.correlation_id,
  };
};
